import Button from "devextreme-react/button";
import CheckBox from "devextreme-react/check-box";
import NumberBox from "devextreme-react/number-box";
import { Popup } from "devextreme-react/popup";
import SelectBox from "devextreme-react/select-box";
import TagBox from "devextreme-react/tag-box";
import TextBox from "devextreme-react/text-box";
import { RequiredRule, StringLengthRule, Validator } from "devextreme-react/validator";
import React, { useEffect, useState } from "react";
import { DefaultPhaseDto } from "../../../../dtos/DefaultPhaseDto";
import { DueDateBasis } from "../../../../dtos/DueDateBasis";
import { GrantStatus } from "../../../../dtos/GrantStatus";
import { PhaseSettings } from "../../../../dtos/PhaseSettings";
import { PhaseWorkflowType } from "../../../../dtos/PhaseWorkflowType";
// import { TrackedMode } from "../../../../dtos/TrackedMode";
import { UserRole } from "../../../../dtos/UserRole";
import { WORKFLOW_TYPE_LABELS, WORKFLOW_TYPE_ORDER, useWorkflowTypeDescriptions } from "../../../tasklist/useWorkflowTypeInfo";

type WorkflowTypeItem = { id: PhaseWorkflowType; label: string; description: string };

// Dropdown row: type name on top with its description underneath so the meaning of each
// workflow type is clear while choosing.
function renderWorkflowTypeItem(item: WorkflowTypeItem) {
    return (
        <div style={{ padding: "2px 0", whiteSpace: "normal" }}>
            <div style={{ fontWeight: 600 }}>{item.label}</div>
            {item.description && (
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                    {item.description}
                </div>
            )}
        </div>
    );
}

type Props = {
    visible: boolean;
    onClose: () => void;
    onSave: (task: DefaultPhaseDto) => void;
    initialValues?: DefaultPhaseDto;
};

export default function AddDefaultTaskDialog({ visible, onClose, onSave, initialValues }: Props) {
    const [name, setName] = useState("");
    const [abbrev, setAbbrev] = useState("");
    const [key, setKey] = useState("");
    const [dueAmount, setDueAmount] = useState<number>(0);
    const [dueUnit, setDueUnit] = useState<'days' | 'weeks' | 'months'>('weeks');
    const [dueDateBasis, setDueDateBasis] = useState<DueDateBasis>(DueDateBasis.ModuleCreation);
    const [tracked, setTracked] = useState<boolean>(false);
    // const [trackedMode, setTrackedMode] = useState<TrackedMode | null>(null);
    const [workflowType, setWorkflowType] = useState<PhaseWorkflowType>(PhaseWorkflowType.Phase);
    const [visibleOnGrantStatuses, setVisibleOnGrantStatuses] = useState<GrantStatus[]>([]);
    // Where this task's files land, and what happens to a repeated name. Set once, at creation:
    // changing it later would leave the files already in SharePoint sitting where the old setting
    // put them, with the database saying otherwise.
    // Root upload is deliberately not offered - see the property in Shared.cs. Record Documents
    // already holds files at the phase root in production, so it keeps working there, but nothing
    // new should adopt it. The value still rides through from an existing task so editing one does
    // not drop it.
    const [rootUpload, setRootUpload] = useState<boolean>(false);
    const [duplicateFileNames, setDuplicateFileNames] = useState<boolean>(false);
    // Which of the two ways the task works, fixed here for the same reason as the file settings
    // above: it decides what every subtask is, and the subtasks a task already has keep the
    // statuses and transitions they were created with. Turning it on later would hide statuses
    // that still count; turning it off would show statuses with no way to advance them.
    const [uploadOnly, setUploadOnly] = useState<boolean>(false);

    const isExistingTask = !!initialValues?.id;

    // Descriptions load from the backend; until then items show the label only.
    const workflowTypeDescriptions = useWorkflowTypeDescriptions();
    const workflowTypeItems: WorkflowTypeItem[] = WORKFLOW_TYPE_ORDER.map((value) => ({
        id: value,
        label: WORKFLOW_TYPE_LABELS[value],
        description: workflowTypeDescriptions.get(value) ?? ""
    }));
    const selectedWorkflowDescription = workflowTypeDescriptions.get(workflowType) ?? "";

    useEffect(() => {
        if (visible) {
            setName(initialValues?.name ?? "");
            setAbbrev(initialValues?.abbrev ?? "");
            setKey(initialValues?.key ?? "");
            setTracked(initialValues?.settings?.tracked ?? false);
            // setTrackedMode(initialValues?.settings?.trackedMode ?? null);
            setWorkflowType(initialValues?.settings?.workflowType ?? PhaseWorkflowType.Phase);
            setDueDateBasis(initialValues?.settings?.dueDateBasis ?? DueDateBasis.ModuleCreation);
            setVisibleOnGrantStatuses(initialValues?.settings?.visibleOnGrantStatuses ?? []);
            setRootUpload(initialValues?.settings?.allowRootUpload ?? false);
            setUploadOnly(initialValues?.settings?.uploadOnlySubtasks ?? false);
            setDuplicateFileNames(initialValues?.settings?.allowDuplicateFileNames ?? false);

            // Determine which unit is being used
            if (initialValues?.settings?.dueDate?.days) {
                setDueAmount(initialValues.settings.dueDate.days);
                setDueUnit('days');
            } else if (initialValues?.settings?.dueDate?.weeks) {
                setDueAmount(initialValues.settings.dueDate.weeks);
                setDueUnit('weeks');
            } else if (initialValues?.settings?.dueDate?.months) {
                setDueAmount(initialValues.settings.dueDate.months);
                setDueUnit('months');
            } else {
                setDueAmount(0);
                setDueUnit('weeks');
            }
        }
    }, [visible, initialValues]);

    const handleSave = () => {
        const trimmedName = name.trim();
        const trimmedAbbrev = abbrev.trim();
        const trimmedKey = key.trim();

        if (!trimmedName) return;

        const dto: DefaultPhaseDto = {
            id: initialValues?.id ?? 0,
            name: trimmedName,
            abbrev: trimmedAbbrev,
            key: trimmedKey,
            moduleTypeId: initialValues?.moduleTypeId ?? 0,
            number: initialValues?.number ?? 0,
            stepTypes: initialValues?.stepTypes ?? [],
            settings: {
                ...(initialValues?.settings ?? {}),
                tracked,
                // trackedMode: tracked ? trackedMode : null,
                workflowType,
                dueDate: {
                    days: dueUnit === 'days' ? dueAmount : null,
                    weeks: dueUnit === 'weeks' ? dueAmount : null,
                    months: dueUnit === 'months' ? dueAmount : null
                },
                dueDateBasis,
                allowRootUpload: rootUpload,
                allowDuplicateFileNames: duplicateFileNames,
                // Only a None task can be a document store - every other type's subtasks carry the
                // workflow that defines the type.
                uploadOnlySubtasks: workflowType === PhaseWorkflowType.None && uploadOnly,
                visibleOnGrantStatuses: visibleOnGrantStatuses.length > 0 ? visibleOnGrantStatuses : null
            } as PhaseSettings,
            deleted: initialValues?.deleted ?? false,
            hasInstances: initialValues?.hasInstances ?? false,
            dateCreated: initialValues?.dateCreated ?? new Date(),
            dateUpdated: new Date(),
        };

        onSave(dto);

        onClose();
    };

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            showCloseButton
            dragEnabled
            title={initialValues ? "Edit Default Task" : "Add Default Task"}
            width={850}
            height="auto"
        >
            <div className="flex flex-col gap-4 p-2">
                {/* Row 1: Task Name and Tracked checkbox */}
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                        <label htmlFor="task-name-input">Task Name</label>
                        <TextBox
                            value={name}
                            onValueChanged={(e) => setName(e.value)}
                            width="100%"
                            inputAttr={{ id: "task-name-input", "aria-label": "Task Name" }}
                        >
                            <Validator>
                                <RequiredRule message="Task name is required" />
                            </Validator>
                        </TextBox>
                    </div>
                    <div style={{ paddingBottom: "8px" }}>
                        <CheckBox
                            value={tracked}
                            onValueChanged={(e) => setTracked(e.value)}
                            text="Tracked"
                            disabled={isExistingTask}
                        />
                    </div>
                    {/* TrackedMode dropdown - disabled until rules for custom tracked tasks are defined
                    <div style={{ flex: 1 }}>
                        <label htmlFor="tracked-mode-select">Tracked Mode</label>
                        <SelectBox
                            items={[
                                { id: TrackedMode.Manual, name: 'Manual (no restrictions)' },
                                { id: TrackedMode.NoOverlap, name: 'No Overlap (validate date ranges)' },
                                { id: TrackedMode.EndDateOnly, name: 'End Date Only (auto-cascade)' }
                            ]}
                            displayExpr="name"
                            valueExpr="id"
                            value={trackedMode}
                            onValueChanged={(e) => setTrackedMode(e.value)}
                            width="100%"
                            placeholder="Select mode..."
                            disabled={!tracked || isExistingTask}
                            inputAttr={{ id: "tracked-mode-select", "aria-label": "Tracked Mode" }}
                        />
                    </div>
                    */}
                </div>

                {/* Row 2: Abbrev */}
                <div style={{ display: "flex", gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                        <label htmlFor="abbrev-input">Abbrev (max 8 chars)</label>
                        <TextBox
                            value={abbrev}
                            onValueChanged={(e) => setAbbrev(e.value)}
                            width="100%"
                            maxLength={8}
                            inputAttr={{ id: "abbrev-input", "aria-label": "Abbrev" }}
                        >
                            <Validator>
                                <RequiredRule message="Abbrev is required" />
                                <StringLengthRule max={8} message="Max 8 characters" />
                            </Validator>
                        </TextBox>
                    </div>
                </div>

                {/* Row 3: Due Date Offset, Offset Unit, Offset From (side by side) */}
                <div style={{ display: "flex", gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                        <label htmlFor="offset-amount-input">DueDate Offset Amount</label>
                        <NumberBox
                            value={dueAmount}
                            onValueChanged={(e) => setDueAmount(Number(e.value))}
                            width="100%"
                            inputAttr={{ id: "offset-amount-input", "aria-label": "DueDate Offset Amount" }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label htmlFor="offset-unit-select">Offset Unit</label>
                        <SelectBox
                            items={['days', 'weeks', 'months']}
                            value={dueUnit}
                            onValueChanged={(e) => setDueUnit(e.value)}
                            width="100%"
                            inputAttr={{ id: "offset-unit-select", "aria-label": "Offset Unit" }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label htmlFor="offset-basis-select">Offset From</label>
                        <SelectBox
                            items={[
                                { id: DueDateBasis.ModuleCreation, name: 'Module Creation' },
                                { id: DueDateBasis.NoaDate, name: 'NoA Date' }
                            ]}
                            displayExpr="name"
                            valueExpr="id"
                            value={dueDateBasis}
                            onValueChanged={(e) => setDueDateBasis(e.value)}
                            width="100%"
                            inputAttr={{ id: "offset-basis-select", "aria-label": "Offset From" }}
                        />
                    </div>
                </div>

                {/* Row 4: Workflow Type (full width) */}
                <div>
                    <label htmlFor="workflow-type-select">Workflow Type</label>
                    <SelectBox
                        items={workflowTypeItems}
                        displayExpr="label"
                        valueExpr="id"
                        value={workflowType}
                        onValueChanged={(e) => setWorkflowType(e.value)}
                        width="100%"
                        disabled={isExistingTask}
                        itemRender={renderWorkflowTypeItem}
                        inputAttr={{ id: "workflow-type-select", "aria-label": "Workflow Type" }}
                    />
                    {selectedWorkflowDescription && (
                        <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                            {selectedWorkflowDescription}
                        </div>
                    )}
                </div>

                {/* Row 4: Visible on Grant Statuses (full width) */}
                <div>
                    <label htmlFor="grant-status-select">Visible on Grant Statuses</label>
                    <TagBox
                        items={[
                            { id: GrantStatus.AWARDED, name: 'AWARDED' },
                            { id: GrantStatus.APPLICATION, name: 'APPLICATION' },
                            { id: GrantStatus.COUNCILREVIEW, name: 'COUNCILREVIEW' },
                            { id: GrantStatus.TOBEPAID, name: 'TOBEPAID' }
                        ]}
                        displayExpr="name"
                        valueExpr="id"
                        value={visibleOnGrantStatuses}
                        onValueChanged={(e) => setVisibleOnGrantStatuses(e.value ?? [])}
                        width="100%"
                        placeholder="Leave empty to always show..."
                        showSelectionControls={true}
                        applyValueMode="useButtons"
                        inputAttr={{ id: "grant-status-select", "aria-label": "Visible on Grant Statuses" }}
                    />
                </div>

                {/* Row 5: File handling and the subtask mode. All locked after creation - see the
                    notes on the state above. */}
                <div>
                    <label>Files</label>
                    <div style={{ display: "grid", gap: "8px", marginTop: "4px" }}>
                        {workflowType === PhaseWorkflowType.None && (
                            <CheckBox
                                value={uploadOnly}
                                onValueChanged={(e) => setUploadOnly(e.value)}
                                text="Subtasks are document folders"
                                hint="Each subtask is a folder holding files, with no status, due date, description or Mark as Complete, so nothing rolls up and the task's status follows its start and completion dates instead."
                                disabled={isExistingTask}
                                elementAttr={{ "data-testid": "upload-only-subtasks" }}
                            />
                        )}
                        <CheckBox
                            value={duplicateFileNames}
                            onValueChanged={(e) => setDuplicateFileNames(e.value)}
                            text="Allow repeated file names (a second copy uploads as name_1)"
                            disabled={isExistingTask}
                            elementAttr={{ "data-testid": "allow-duplicate-names" }}
                        />
                        {isExistingTask && (
                            <span style={{ fontSize: "12px", color: "#6b7280" }}>
                                Set when the task is created. Changing it afterward would leave files
                                already in SharePoint where the old setting put them.
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                    <Button text="Cancel" stylingMode="outlined" onClick={onClose} />
                    <Button text="Save" type="default" stylingMode="contained" onClick={handleSave} />
                </div>
            </div>
        </Popup>
    );
}
